"use client";

import Image from "next/image";
import Link from "next/link";

import { Text } from "@/components/text/text.component";

import { resolveQuizType } from "@/lib/analytics/quiz-analytics";

import { QUIZ_PAGE } from "@/constants/page.constants";
import { useTagging } from "@/hooks/useTagging";
import { urlFor } from "@/sanity/utils";

export const QuizSection = ({ block }: QuizSectionProps) => {
  const previewStep = block.steps?.[0];
  const [, setTag] = useTagging();

  const handleOptionClick = (option: QuizStepOption, question: string) => {
    const quizType = resolveQuizType({
      startQuestionKey: option.quizStartKey,
      answer: option.title,
    });
    const questionText = question.includes("?")
      ? question.slice(0, question.indexOf("?") + 1)
      : question;

    setTag({
      event: "quiz_start_event",
      "Quiz-Type": quizType,
      "Quiz-Step": "1",
      "Quiz-Question": questionText,
      "Quiz-Answers": option.title,
    });
    setTag({
      event: "quiz_event",
      "Quiz-Type": quizType,
      "Quiz-Step": "1",
      "Quiz-Question": questionText,
      "Quiz-Answers": option.title,
    });
  };

  const renderImage = () => {
    const asset = block.image?.asset;
    if (!asset) return null;
    const imageUrl =
      asset.url ??
      (asset._ref ? urlFor(asset).width(800).height(700).url() : null);
    if (!imageUrl) return null;

    return (
      <div
        data-testid="quiz-section-image"
        className="hidden lg:relative lg:-my-16 lg:flex lg:w-full lg:max-w-sm lg:shrink-0 lg:self-stretch lg:justify-self-start lg:overflow-hidden">
        <Image
          src={imageUrl}
          alt={block.image?.alt ?? "ADT quiz section"}
          fill
          className="object-cover object-center"
          sizes="(min-width: 1024px) 384px, 100vw"
        />
      </div>
    );
  };

  const renderMobileImage = () => {
    const asset = block.imageMobile?.asset;
    if (!asset) return null;
    const imageUrl =
      asset.url ??
      (asset._ref ? urlFor(asset).width(500).height(300).url() : null);
    if (!imageUrl) return null;

    return (
      <div
        data-testid="quiz-section-image-mobile"
        className="relative w-full overflow-hidden lg:hidden">
        <Image
          src={imageUrl}
          alt={block.imageMobile?.alt ?? "ADT quiz section"}
          width={800}
          height={400}
          className="h-auto w-full object-cover"
          sizes="100vw"
        />
      </div>
    );
  };

  const renderStepPreview = (step: QuizStep) => {
    return (
      <div
        data-testid="quiz-section-step-preview"
        className="flex flex-col gap-8">
        <div className="flex flex-col gap-4">
          {step.stepLabel && (
            <Text
              data-testid="quiz-section-step-label"
              component="h2"
              variant="subtitle1"
              className="text-primary font-bold tracking-widest uppercase">
              {step.stepLabel}
            </Text>
          )}

          <Text
            variant="h5"
            component="h3"
            data-testid="quiz-section-step-question"
            className="!text-[24px] font-bold text-white! lg:!text-[24px]">
            {step.question}
          </Text>
        </div>

        {step.options && step.options.length > 0 && (
          <div className="flex flex-col gap-3">
            {step.options.map((option, index) => {
              const iconUrl = option.icon?.asset
                ? urlFor(option.icon.asset).width(40).height(40).url()
                : null;

              const optionUrl = option.quizStartKey
                ? `${QUIZ_PAGE}?start=${option.quizStartKey}&source=hp`
                : null;

              const cardContent = (
                <>
                  {iconUrl && (
                    <div className="relative h-12 w-12 shrink-0">
                      <Image
                        src={iconUrl}
                        alt={`Quiz section option icon for ${option.title}`}
                        fill
                        className="object-contain p-1"
                        sizes="36px"
                      />
                    </div>
                  )}
                  <div className="flex flex-col gap-1">
                    <p
                      data-testid="quiz-section-option-title"
                      className="text-base text-white">
                      {option.title}
                    </p>
                    {option.description && (
                      <p
                        data-testid="quiz-section-option-description"
                        className="text-sm leading-snug text-white/70">
                        {option.description}
                      </p>
                    )}
                  </div>
                </>
              );

              if (optionUrl) {
                return (
                  <Link
                    key={option._key ?? index}
                    href={optionUrl}
                    onClick={() => handleOptionClick(option, step.question)}
                    data-testid="quiz-section-option-card"
                    className="bg-secondary-100 hover:bg-secondary-100/80 flex items-start gap-3 rounded-2xl border border-white/15 p-4 transition-colors hover:border-white/30">
                    {cardContent}
                  </Link>
                );
              }

              return (
                <div
                  key={option._key ?? index}
                  data-testid="quiz-section-option-card"
                  className="bg-secondary-100 flex items-center gap-3 rounded-2xl border border-white/15 p-4">
                  {cardContent}
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <section
      data-testid="quiz-section"
      className="bg-secondary relative w-full overflow-hidden py-12 lg:py-16">
      {renderMobileImage()}
      <div className="container mx-auto px-8">
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[1fr_1.2fr_1.4fr] lg:items-center lg:gap-10">
          {renderImage()}

          <div
            data-testid="quiz-section-content"
            className="flex flex-col gap-4">
            <Text
              variant="h3"
              component="h2"
              data-testid="quiz-section-heading"
              className="text-white!">
              {block.heading}
            </Text>

            {block.supportingCopy && (
              <Text
                variant="body1"
                data-testid="quiz-section-supporting-copy"
                className="text-white/80">
                {block.supportingCopy}
              </Text>
            )}
          </div>

          {previewStep && renderStepPreview(previewStep)}
        </div>
      </div>
    </section>
  );
};

type QuizStepOption = {
  _key?: string;
  title: string;
  description?: string;
  icon?: {
    asset?: {
      _ref?: string;
      url?: string;
    };
  };
  quizStartKey?: string;
};

type QuizStep = {
  _key?: string;
  stepLabel?: string;
  question: string;
  options?: QuizStepOption[];
};

type QuizSectionBlock = {
  _type: "quizSection";
  _key?: string;
  heading: string;
  supportingCopy?: string;
  image?: {
    asset?: {
      _ref?: string;
      url?: string;
    };
    alt?: string;
  };
  imageMobile?: {
    asset?: {
      _ref?: string;
      url?: string;
    };
    alt?: string;
  };
  steps?: QuizStep[];
};

type QuizSectionProps = {
  block: QuizSectionBlock;
};
